import type { ArtistParametersDto } from '@customTypes/types';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import {
  useNavigate,
  useParams,
  useRouter,
  useSearch
} from '@tanstack/react-router';
import { useEffect } from 'react';

import {
  useArtistProfileParametersMutation,
  useArtistProfileParametersQuery
} from '@/shared/api/requests';

import { validateParameters } from '../constants/validateParameters';

export const useArtistParameters = () => {
  const navigate = useNavigate({ from: '/artist/$id/onboard/$step' });
  const search = useSearch({ from: '/artist/$id/_onboard/onboard/$step' });
  const router = useRouter();
  const queryClient = useQueryClient();
  const { id: profileId } = useParams({ from: '/artist/$id' });
  const artistParameters = useArtistProfileParametersQuery({ profileId });

  const queries = [artistParameters];

  const isLoading = queries.some((q) => q.isLoading);

  const next = () => {
    if (search.from !== undefined) {
      router.history.back();
    }
    navigate({
      to: '/artist/$id/onboard/$step',
      params: { step: 'photo' }
    });
  };

  const artistParamsMutation = useArtistProfileParametersMutation({
    options: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['ArtistProfileParameters', profileId]
        });
        next();
      }
    }
  });

  const form = useForm<ArtistParametersDto>({
    validateInputOnBlur: true,
    validate: zodResolver(validateParameters)
  });

  useEffect(() => {
    if (artistParameters.isSuccess) {
      form.initialize(artistParameters.data.data);
    }
  }, [artistParameters.data?.data]);

  const onSubmit = form.onSubmit(async (values) => {
    await artistParamsMutation.mutateAsync({
      params: {
        profileId,
        data: values
      }
    });
  });

  const isFormValid =
    form.isValid('hips') &&
    form.isValid('waist') &&
    form.isValid('weight') &&
    form.isValid('chest') &&
    form.isValid('height');

  return {
    state: {
      isLoading,
      form,
      isFormValid
    },
    functions: { onSubmit }
  };
};
